import { useMemo } from 'react';
import { useQueryClient, useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import {
    fetchDocuments,
    fetchDocumentTypes,
    fetchProductCategories,
    fetchDocumentFile,
    deleteDocument,
    restoreDocument,
    fetchDocumentHistory,
} from 'modules/Documents/api/documents';
import { DocumentHistoryEvent, DocumentHistoryQueryArgument } from 'modules/Documents/interfaces/documents';
import DocumentType from 'modules/Documents/interfaces/DocumentType.interface';
import { useFiltersContext } from 'modules/Documents/FiltersContext';
import { Document, DocumentResponse } from 'modules/common/interfaces/documents';
import openDocument from 'modules/Documents/openDocument';
import { usePreviewDocumentMutation as useBasePreviewDocumentMutation } from 'modules/common/queries/documents';
import ProductCategory from 'modules/Documents/interfaces/ProductCategory.interface';
import buildDocumentSearch, { buildSearchFilters } from 'modules/Documents/buildDocumentSearch';
import { useAuthContext } from 'modules/Auth/AuthProvider';
import useStatsigGate from 'modules/common/useStatsigGate';
import { FeatureGates } from 'modules/common/constants/featuregates';

export const usePreviewDocumentMutation = (isAllowed = false) => {
    const { state: filters } = useFiltersContext();
    return useBasePreviewDocumentMutation(filters?.isEmployeeView || isAllowed);
};

export const useDownloadDocumentMutation = (isAllowed = false) => {
    const { state: filters } = useFiltersContext();
    const mutation = useMutation({
        mutationFn: async (document: Document) => fetchDocumentFile(filters?.isEmployeeView || isAllowed, document),

        onSuccess: ({ data, document: { file_name } }) => {
            openDocument(data, file_name, true);
        },
    });

    return mutation;
};

export const useDeleteDocumentMutation = (onSuccess?: () => void) => {
    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationFn: async (document: Document) => deleteDocument(document),

        onSuccess: () => {
            queryClient.refetchQueries({
                queryKey: ['searchDocuments'],
            });
            onSuccess?.();
        },
    });

    return mutation;
};

export const useRestoreDocumentMutation = () => {
    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationFn: async (document: Document) => restoreDocument(document),

        onSuccess: () => {
            queryClient.refetchQueries({
                queryKey: ['searchDocuments'],
            });
        },
    });

    return mutation;
};

export const useDocumentTypes = <T = DocumentType[]>(
    options?: Omit<UseQueryOptions<DocumentType[], AxiosError, T>, 'queryKey'>
) => {
    const { select, ...opts } = options || {};

    return useQuery({
        queryKey: ['documentTypes'],
        queryFn: fetchDocumentTypes,
        staleTime: Infinity,
        placeholderData: [],
        select: (data) => {
            const docTypes = data.filter((dt) => dt.id !== 'AWAITING_IDENTIFICATION');
            return select ? select(docTypes) : docTypes;
        },
        ...opts,
    });
};

// Stands in until the first response, so callers can read results without guarding for it.
const EMPTY_DOCUMENT_RESPONSE: DocumentResponse = {
    total_records: 0,
    page_size: 0,
    total_pages: 0,
    current_page: 0,
    results: [],
};

export const useDocuments = () => {
    const { state } = useFiltersContext();
    const { data: documentTypes } = useDocumentTypes();
    const isPGParser = useStatsigGate(FeatureGates.DocumentUsePGParser);

    const { member_uuid: memberUUID, ...filters } = state;

    const querySearch = useMemo(() => {
        // this is to prevent re renders if we use filter from outside
        const isInternal = !state.isEmployeeView ? [false] : state.is_internal;

        if (isPGParser) {
            return buildSearchFilters({ ...state, is_internal: isInternal });
        }

        return buildDocumentSearch({
            ...state,
            is_internal: isInternal,
        });
    }, [state, isPGParser]);

    const { data: documents = EMPTY_DOCUMENT_RESPONSE, ...rest } = useQuery<DocumentResponse>({
        queryKey: ['searchDocuments', querySearch, memberUUID, filters.page, filters.page_size],
        queryFn: () => fetchDocuments({ state, querySearch }),
        staleTime: 60 * 1000 * 5, // 5 minutes
        select: (data) => ({
            ...data,
            results: data.results.map((result) => ({
                ...result,
                // The search response carries no type data: undefined until the types load, or for an unknown type.
                document_type_data:
                    documentTypes?.find((type) => type.id === result.document_type) ?? result.document_type_data,
            })),
        }),
    });

    return { data: documents, ...rest } as const;
};

export const useDocumentType = (id: string) =>
    useDocumentTypes<DocumentType>({
        select: (data) => data.find((type) => type.id === id) as DocumentType,
    });

// Omits `queryKey` like the `useDocumentTypes` it delegates to, which is what supplies the key.
export const useScopedDocumentTypes = (
    options?: Omit<UseQueryOptions<DocumentType[], AxiosError, DocumentType[]>, 'queryKey'>
) => {
    const {
        state: { user },
    } = useAuthContext();
    const { select, ...rest } = options || {};

    return useDocumentTypes({
        ...rest,
        select: (data) => {
            const results = user?.is_employee ? data : data.filter((dt) => !dt.is_internal);
            return select ? select(results) : results;
        },
    });
};

export const useProductCategories = <T = ProductCategory[]>(
    options?: UseQueryOptions<ProductCategory[], AxiosError, T>
) =>
    useQuery({
        queryKey: ['productCategories'],
        queryFn: () => fetchProductCategories(),
        staleTime: Infinity,
        placeholderData: [],
        ...options,
    });

export const useDocumentHistory = (data: DocumentHistoryQueryArgument, options = {}) =>
    useQuery<DocumentHistoryEvent[]>({
        queryKey: ['documentHistory', data],
        queryFn: () => fetchDocumentHistory(data),
        placeholderData: [],
        refetchOnMount: true,
        ...options,
    });
